// Library Methods
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { ChevronDownIcon } from "src/assets/icons";
import { ACCENT_TEAL } from "src/pages/EmrConnections/ChoiceCard/ChoiceCard";
import {
  GUIDE_NAMESPACE,
  GUIDE_SECTION_PREFIX,
  scrollToGuideSection,
} from "src/components/containers/KnowledgeHub/DataHubGuide/GuideText/GuideText";
import {
  BeforeChapter,
  FindChapter,
  WhatChapter,
} from "src/components/containers/KnowledgeHub/DataHubGuide/Chapters/StartChapters";
import {
  AddHubChapter,
  HubCardChapter,
  InstallChapter,
} from "src/components/containers/KnowledgeHub/DataHubGuide/Chapters/SetupChapters";
import {
  EditPacChapter,
  GroupsChapter,
  MachineChapter,
  ReportsChapter,
  WorklistChapter,
} from "src/components/containers/KnowledgeHub/DataHubGuide/Chapters/ConnectChapters";
import {
  JobsChapter,
  OutboxChapter,
  ProblemsChapter,
  ProcessedChapter,
  StatusChapter,
  WorklistTodayChapter,
} from "src/components/containers/KnowledgeHub/DataHubGuide/Chapters/DailyChapters";
import {
  FaqChapter,
  ManageChapter,
  UninstallOldChapter,
  UpdateChapter,
  WordsChapter,
} from "src/components/containers/KnowledgeHub/DataHubGuide/Chapters/CareChapters";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

export const GUIDE_CONTENTS = [
  {
    group: "Start here",
    items: [
      { id: "what", label: "What the Data Hub does" },
      { id: "before", label: "Before you start" },
      { id: "find", label: "Find the Data Hub" },
    ],
  },
  {
    group: "Set up the hub",
    items: [
      { id: "add-hub", label: "1. Add a hub" },
      { id: "install", label: "2. Install it on the PC" },
      { id: "hub-card", label: "3. Read the hub card" },
    ],
  },
  {
    group: "Connect things",
    items: [
      { id: "groups", label: "The three groups" },
      { id: "worklist", label: "Worklist" },
      { id: "machine", label: "Machine" },
      { id: "reports", label: "RetinaLogik Exams" },
      { id: "edit-pac", label: "Change a PAC server" },
    ],
  },
  {
    group: "Day to day",
    items: [
      { id: "status", label: "Is it working?" },
      { id: "worklist-today", label: "Patients imported today" },
      { id: "outbox", label: "Report Outbox" },
      { id: "processed", label: "Processed Exams" },
      { id: "jobs", label: "Jobs" },
      { id: "problems", label: "Problems and fixes" },
    ],
  },
  {
    group: "Look after it",
    items: [
      { id: "update", label: "Update the hub" },
      { id: "manage", label: "Rename, share, delete" },
      { id: "faq", label: "Questions" },
      { id: "words", label: "Words explained" },
    ],
  },
  {
    group: "Addendum",
    items: [{ id: "uninstall-old", label: "Remove the old version" }],
  },
];

const WIDE = "@media (min-width: 1000px)";

const SECTION_IDS = GUIDE_CONTENTS.flatMap((group) =>
  group.items.map((item) => item.id)
);

const useCurrentSection = () => {
  const [current, setCurrent] = useState(SECTION_IDS[0]);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible[0]) {
          setCurrent(visible[0].target.id.replace(GUIDE_SECTION_PREFIX, ""));
        }
      },
      { rootMargin: "0px 0px -70% 0px" }
    );

    SECTION_IDS.forEach((id) => {
      const element = document.getElementById(`${GUIDE_SECTION_PREFIX}${id}`);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  return current;
};

const Contents = () => {
  const { t } = useTranslation(GUIDE_NAMESPACE);
  const theme = useTheme();
  const { mutedText, surface, dividerBorder } = getSurfaceTokens(theme);
  const isDark = theme.palette.mode === "dark";
  const current = useCurrentSection();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Box
      component="nav"
      aria-label={t("Contents")}
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        alignSelf: "start",
        backgroundColor: surface,
        border: dividerBorder,
        borderRadius: "8px",
        padding: "6px 10px",
        fontSize: "13px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
        [WIDE]: {
          top: "16px",
          backgroundColor: "transparent",
          border: 0,
          padding: 0,
          boxShadow: "none",
          maxHeight: "calc(100vh - 32px)",
          overflowY: "auto",
        },
      }}
    >
      <Box
        component="button"
        type="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        sx={{
          font: "inherit",
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "none",
          border: 0,
          padding: "8px 0",
          cursor: "pointer",
          [WIDE]: { cursor: "default" },
          color: ACCENT_TEAL,
          fontWeight: 700,
          fontSize: "12px",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}
      >
        {t("Contents")}
        <Box display="flex" sx={{ [WIDE]: { display: "none" } }}>
          <ChevronDownIcon width="16px" height="16px" />
        </Box>
      </Box>
      <Box
        component="ol"
        sx={{
          listStyle: "none",
          margin: 0,
          padding: 0,
          display: isOpen ? "flex" : "none",
          flexDirection: "column",
          gap: "1px",
          maxHeight: "60vh",
          overflowY: "auto",
          [WIDE]: { display: "flex", maxHeight: "none", overflowY: "visible" },
        }}
      >
        {GUIDE_CONTENTS.map((group) => (
          <Box component="li" key={group.group}>
            <Typography
              fontSize="11px"
              letterSpacing="0.08em"
              textTransform="uppercase"
              color={mutedText}
              padding="10px 10px 4px"
            >
              {t(group.group)}
            </Typography>
            <Box
              component="ol"
              sx={{ listStyle: "none", margin: 0, padding: 0 }}
            >
              {group.items.map((item) => {
                const isCurrent = item.id === current;

                return (
                  <li key={item.id}>
                    <Box
                      component="button"
                      type="button"
                      aria-current={isCurrent ? "location" : undefined}
                      onClick={() => {
                        scrollToGuideSection(item.id);
                        setIsOpen(false);
                      }}
                      sx={{
                        font: "inherit",
                        width: "100%",
                        textAlign: "left",
                        background: isCurrent
                          ? isDark
                            ? "#2A2C3F"
                            : "rgba(27, 143, 118, 0.08)"
                          : "none",
                        border: 0,
                        borderLeft: `3px solid ${isCurrent ? ACCENT_TEAL : "transparent"}`,
                        borderRadius: "6px",
                        padding: "5px 10px",
                        cursor: "pointer",
                        color: isCurrent ? ACCENT_TEAL : "inherit",
                        fontWeight: isCurrent ? 700 : 400,
                        "&:hover": {
                          backgroundColor: isDark ? "#5B6D7A40" : "#5B6D7A20",
                        },
                        "&:focus-visible": { outline: "3px solid #35BCFD" },
                      }}
                    >
                      {t(item.label)}
                    </Box>
                  </li>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const DataHubGuide = () => {
  const { t } = useTranslation(GUIDE_NAMESPACE);

  return (
    <Box display="flex" flexDirection="column" gap="16px">
      <Box>
        <Typography component="h1" fontSize="20px" fontWeight={700}>
          {t("Data Hub Guide")}
        </Typography>
        <Typography fontSize="14px" mt="4px">
          {t(
            "How to connect your clinic's machines, schedule and archive to RetinaLogik, step by step. Written for everyone at the clinic, no technical background needed."
          )}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr)",
          gap: "8px",
          [WIDE]: { gridTemplateColumns: "230px minmax(0, 1fr)", gap: "40px" },
          alignItems: "start",
        }}
      >
        <Contents />
        <Box minWidth={0} maxWidth="980px">
          <WhatChapter />
          <BeforeChapter />
          <FindChapter />
          <AddHubChapter />
          <InstallChapter />
          <HubCardChapter />
          <GroupsChapter />
          <WorklistChapter />
          <MachineChapter />
          <ReportsChapter />
          <EditPacChapter />
          <StatusChapter />
          <WorklistTodayChapter />
          <OutboxChapter />
          <ProcessedChapter />
          <JobsChapter />
          <ProblemsChapter />
          <UpdateChapter />
          <ManageChapter />
          <FaqChapter />
          <WordsChapter />
          <UninstallOldChapter />
        </Box>
      </Box>
    </Box>
  );
};

export default DataHubGuide;
